/**
 * ui.ts - Tournament UI interactions
 *
 * Handles UI interactions including:
 * - Toggle visibility of tournament sections
 * - Tab switching
 * - Player highlighting
 */

/**
 * Toggle tournament content visibility
 * @param tournamentId - Tournament ID
 */
export function toggleTournamentContent(tournamentId: string): void {
  const content = document.getElementById(`tournament-content-${tournamentId}`);
  const toggleIcon = document.getElementById(`tournament-toggle-icon-${tournamentId}`);

  if (!content || !toggleIcon) return;

  const isOpening = content.style.display === 'none';

  if (isOpening) {
    content.style.display = 'block';
    toggleIcon.textContent = '▼';

    // Refresh map if it exists
    setTimeout(() => {
      const map = (window as any)[`tournamentMap_${tournamentId}`];
      if (map && map.invalidateSize) {
        map.invalidateSize();
      }
    }, 50);
  } else {
    content.style.display = 'none';
    toggleIcon.textContent = '▶';
  }
}

/**
 * Toggle tournament details visibility
 * @param tournamentId - Tournament ID
 */
export function toggleTournamentDetails(tournamentId: string): void {
  const detailsInfo = document.getElementById(`tournament-info-${tournamentId}`);
  const toggleIcon = document.getElementById(`details-toggle-icon-${tournamentId}`);

  if (!detailsInfo || !toggleIcon) return;

  if (detailsInfo.style.display === 'none') {
    detailsInfo.style.display = 'block';
    toggleIcon.textContent = '▼';
  } else {
    detailsInfo.style.display = 'none';
    toggleIcon.textContent = '▶';
  }
}

/**
 * Toggle players list visibility
 * @param tournamentId - Tournament ID
 */
export function togglePlayersList(tournamentId: string): void {
  const playersList = document.getElementById(`players-list-${tournamentId}`);
  const toggleIcon = document.getElementById(`players-toggle-icon-${tournamentId}`);

  if (!playersList || !toggleIcon) return;

  if (playersList.style.display === 'none') {
    playersList.style.display = 'block';
    toggleIcon.textContent = '▼';
  } else {
    playersList.style.display = 'none';
    toggleIcon.textContent = '▶';
  }
}

/**
 * Toggle pairings and standings section visibility
 * @param tournamentId - Tournament ID
 */
export function togglePairingsStandings(tournamentId: string): void {
  const content = document.getElementById(`pairings-standings-content-${tournamentId}`);
  const toggleIcon = document.getElementById(`pairings-toggle-icon-${tournamentId}`);

  if (!content || !toggleIcon) return;

  if (content.style.display === 'none') {
    content.style.display = 'block';
    toggleIcon.textContent = '▼';
  } else {
    content.style.display = 'none';
    toggleIcon.textContent = '▶';
  }
}

/**
 * Toggle player form visibility
 * @param tournamentId - Tournament ID
 */
export function togglePlayerForm(tournamentId: string): void {
  const form = document.getElementById(`player-form-${tournamentId}`);
  if (!form) return;

  const isOpening = form.style.display === 'none';
  form.style.display = isOpening ? 'block' : 'none';

  // Auto-focus the first name input when opening the form
  if (isOpening) {
    setTimeout(() => {
      const firstNameInput = document.getElementById(`player-first-name-${tournamentId}`) as HTMLInputElement | null;
      if (firstNameInput) {
        firstNameInput.focus();
      }
    }, 100);
  }
}

/**
 * Toggle view configuration visibility
 */
export function toggleViewConfig(): void {
  const config = document.getElementById('view-config-panel');
  if (!config) return;

  if (config.style.display === 'none') {
    config.style.display = 'block';
  } else {
    config.style.display = 'none';
  }
}

/**
 * Switch between main tournament tabs (Overview, Players, Pairings, Standings)
 * @param tournamentId - Tournament ID
 * @param tabName - Tab name (overview, players, pairings, standings)
 */
export function switchMainTab(tournamentId: string, tabName: string): void {
  // Remove active class from all tab buttons for this tournament
  const allButtons = document.querySelectorAll<HTMLElement>(
    `[data-tab^="overview-${tournamentId}"], [data-tab^="players-${tournamentId}"], [data-tab^="pairings-${tournamentId}"], [data-tab^="standings-${tournamentId}"]`
  );
  allButtons.forEach(btn => btn.classList.remove('active'));

  // Hide all tab panes for this tournament
  document.querySelectorAll<HTMLElement>(
    `#overview-${tournamentId}, #players-tab-${tournamentId}, #pairings-tab-${tournamentId}, #standings-tab-${tournamentId}`
  ).forEach(pane => {
    pane.classList.remove('active');
  });

  // Show the selected tab
  const targetButton = document.querySelector<HTMLElement>(`[data-tab="${tabName}-${tournamentId}"]`);
  if (targetButton) {
    targetButton.classList.add('active');
  }

  // Show the corresponding pane
  const targetPane = document.getElementById(`${tabName === 'overview' ? 'overview' : tabName + '-tab'}-${tournamentId}`);
  if (targetPane) {
    targetPane.classList.add('active');
  }

  // Initialize keyboard shortcuts when switching to pairings tab
  if (tabName === 'pairings') {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      if (window.initKeyboardShortcuts) {
        window.initKeyboardShortcuts(tournamentId);
      }
    }, 100);
  } else {
    // Disable keyboard shortcuts when leaving pairings tab
    if (window.disableKeyboardShortcuts) {
      window.disableKeyboardShortcuts();
    }
  }
}

/**
 * Switch between tabs (generic tab switcher)
 * @param tournamentId - Tournament ID
 * @param targetTabId - Target tab ID
 */
export function switchTab(tournamentId: string, targetTabId: string): void {
  // Get all tab buttons and panes for this tournament
  const tabButtons = document.querySelectorAll<HTMLElement>(
    `[data-tab^="matches-${tournamentId}"], [data-tab^="standings-${tournamentId}"], [data-tab^="ranking-${tournamentId}"]`
  );
  const tabPanes = document.querySelectorAll<HTMLElement>(
    `#matches-${tournamentId}, #standings-${tournamentId}, #ranking-${tournamentId}`
  );

  // Remove active class from all buttons and panes
  tabButtons.forEach(btn => btn.classList.remove('active'));
  tabPanes.forEach(pane => pane.classList.remove('active'));

  // Add active class to clicked button and corresponding pane
  const clickedButton = document.querySelector<HTMLElement>(`[data-tab="${targetTabId}"]`);
  const targetPane = document.getElementById(targetTabId);

  if (clickedButton) clickedButton.classList.add('active');
  if (targetPane) targetPane.classList.add('active');
}

/**
 * Switch between player suggestions tabs (FIDE / Swiss Chess)
 * @param tournamentId - Tournament ID
 * @param tabName - Tab name (fide or swisschess)
 */
export function switchSuggestionsTab(tournamentId: string, tabName: string): void {
  const suggestionsDiv = document.getElementById(`player-suggestions-${tournamentId}`);
  if (!suggestionsDiv) return;

  // Update tab buttons
  const tabButtons = suggestionsDiv.querySelectorAll<HTMLElement>('.suggestions-tab');
  tabButtons.forEach(btn => {
    if (btn.getAttribute('data-tab') === tabName) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });

  // Update tab content
  const fideList = document.getElementById(`suggestions-fide-${tournamentId}`);
  const swissChessList = document.getElementById(`suggestions-swisschess-${tournamentId}`);

  if (!fideList || !swissChessList) return;

  if (tabName === 'fide') {
    fideList.classList.add('active');
    swissChessList.classList.remove('active');
  } else {
    fideList.classList.remove('active');
    swissChessList.classList.add('active');
  }
}

/**
 * Highlight a player in the list
 * @param tournamentId - Tournament ID
 * @param playerId - Player ID
 */
export function highlightPlayer(tournamentId: string, playerId: string): void {
  const playerElement = document.getElementById(`player-${tournamentId}-${playerId}`);

  if (playerElement) {
    // Add highlight class
    playerElement.classList.add('player-highlight');

    // Scroll to the player
    playerElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Remove highlight after 2 seconds
    setTimeout(() => {
      playerElement.classList.remove('player-highlight');
    }, 2000);
  } else {
    console.warn(`Player element not found: player-${tournamentId}-${playerId}`);
  }
}

/**
 * Toggle tournament dropdown menu visibility
 * @param tournamentId - Tournament ID
 */
export function toggleTournamentMenu(tournamentId: string): void {
  const menu = document.getElementById(`tournament-menu-${tournamentId}`);
  if (!menu) return;

  const isCurrentlyVisible = menu.style.display === 'block';

  // Close all other open tournament menus first
  document.querySelectorAll('.tournament-dropdown-menu').forEach((otherMenu) => {
    if (otherMenu !== menu) {
      (otherMenu as HTMLElement).style.display = 'none';
    }
  });

  // Toggle the current menu
  menu.style.display = isCurrentlyVisible ? 'none' : 'block';
}

// Close menu when clicking outside
document.addEventListener('click', (event: MouseEvent) => {
  const target = event.target as HTMLElement;

  // Don't close if clicking the toggle button itself (let onclick handler manage it)
  if (target.closest('.tournament-menu-toggle')) {
    return;
  }

  // Check if click is outside any tournament menu
  if (!target.closest('.tournament-menu-container')) {
    document.querySelectorAll('.tournament-dropdown-menu').forEach((menu) => {
      (menu as HTMLElement).style.display = 'none';
    });
  }
});

// Export to window for onclick handlers

/**
 * Open status dropdown for changing tournament status
 * @param tournamentId - Tournament ID
 * @param event - Click event
 */
export function openStatusDropdown(tournamentId: string, event: MouseEvent): void {
  event.stopPropagation();

  const dropdown = document.getElementById(`status-dropdown-${tournamentId}`);
  if (!dropdown) return;

  // Close all other status dropdowns
  document.querySelectorAll('.status-dropdown').forEach((d) => {
    if (d !== dropdown) {
      (d as HTMLElement).style.display = 'none';
    }
  });

  // Toggle current dropdown
  const isVisible = dropdown.style.display === 'block';
  dropdown.style.display = isVisible ? 'none' : 'block';
}

/**
 * Change tournament status
 * @param tournamentId - Tournament ID
 * @param newStatus - New status value
 * @param event - Click event
 */
export async function changeStatus(tournamentId: string, newStatus: string, event: MouseEvent): Promise<void> {
  event.stopPropagation();

  const dropdown = document.getElementById(`status-dropdown-${tournamentId}`);
  if (dropdown) {
    dropdown.style.display = 'none';
  }

  try {
    const response = await fetch(`/api/tournaments/${tournamentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify({ status: newStatus })
    });

    if (!response.ok) {
      throw new Error('Failed to update status');
    }

    // Update the UI
    const statusBadge = document.querySelector(`.tournament-status[data-tournament-id="${tournamentId}"]`) as HTMLElement;
    if (statusBadge) {
      // Remove old status class
      statusBadge.classList.remove('status-upcoming', 'status-ongoing', 'status-completed');
      // Add new status class
      statusBadge.classList.add(`status-${newStatus}`);
      statusBadge.setAttribute('data-status', newStatus);

      // Update text - get translated text
      const statusKey = `status.${newStatus}`;
      const statusText = window.i18n?.t(statusKey) || newStatus;
      statusBadge.textContent = statusText;
    }

    // Update dropdown active state
    const dropdownOptions = document.querySelectorAll(`#status-dropdown-${tournamentId} .status-dropdown-option`);
    dropdownOptions.forEach((option) => {
      const opt = option as HTMLElement;
      opt.classList.remove('active');
      if (opt.textContent?.toLowerCase().includes(newStatus)) {
        opt.classList.add('active');
      }
    });

  } catch (error) {
    console.error('Error changing status:', error);
    alert('Failed to change status. Please try again.');
  }
}

// Close status dropdown when clicking outside
document.addEventListener('click', (event: MouseEvent) => {
  const target = event.target as HTMLElement;

  // Don't close if clicking the status badge or dropdown container (let onclick handler manage it)
  if (target.closest('.tournament-status') || target.closest('.status-dropdown-container')) {
    return;
  }

  // Close all status dropdowns
  document.querySelectorAll('.status-dropdown').forEach((dropdown) => {
    (dropdown as HTMLElement).style.display = 'none';
  });
});

window.toggleTournamentContent = toggleTournamentContent;
window.toggleTournamentDetails = toggleTournamentDetails;
window.togglePlayersList = togglePlayersList;
window.togglePairingsStandings = togglePairingsStandings;
window.togglePlayerForm = togglePlayerForm;
window.toggleViewConfig = toggleViewConfig;
window.toggleTournamentMenu = toggleTournamentMenu;
window.switchMainTab = switchMainTab;
window.switchTab = switchTab;
window.switchSuggestionsTab = switchSuggestionsTab;
window.highlightPlayer = highlightPlayer;
window.openStatusDropdown = openStatusDropdown;
window.changeStatus = changeStatus;
